import { Model, Factory, hasMany } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { rand, randCompanyName, randHex } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer } from './server.js'
import { filterable } from './utils.js'

export type DepartmentData = SchemaAttributes<'Department'>

export const model = (Model as ModelDefinition<DepartmentData>).extend({
  featureBlocks: hasMany('featureBlock'),
})

export const factory = Factory.extend<DepartmentData>({
  name() {
    return randCompanyName()
  },
  archived() {
    return false
  },
  navbar_color() {
    return randHex()
  },
  country() {
    return rand(['GB', 'NL', 'FI', 'US'])
  },
  timezone() {
    switch (this.country) {
      case 'NL':
        return 'Europe/Amsterdam'
      case 'FI':
        return 'Europe/Helsinki'
      case 'GB':
        return 'Europe/London'
      case 'US':
        return rand([
          'America/New_York',
          'America/Denver',
          'America/Los_Angeles',
        ])
      default:
        return 'UTC'
    }
  },
  afterCreate(department, server) {
    if (!department.featureBlocks.length) {
      const featureBlocks = [
        server.create('featureBlock', {
          id: `Task-${department.id}`,
          name: 'task',
          visible: true,
        }),
      ]
      department.update({ featureBlocks })
    }
  },
})

export function routes(server: MockServer) {
  server.resource('department')

  server.get('/departments', function (schema, request) {
    const departments = schema.all('department')

    const filtered = filterable(departments, request, {
      'id.in': (item, value) => String(item.id) === String(value),
      archived: (item, value) => {
        const filterValue = value === 'true'
        return Boolean(item.attrs.archived) === filterValue
      },
      available_to_user: (item, value) => {
        // Return all departments when filter[available_to_user]=true
        return value === 'true'
      },
    })

    return filtered
  })

  server.get('/departments/:id/feature_blocks', function (schema, request) {
    const departmentId = request.params.id
    const department = schema.find('department', departmentId)

    if (!department) {
      return { data: [] }
    }

    return department.featureBlocks
  })
}

export function seeds(server: MockServer) {
  server.create('department', { name: 'Bath Clinic', navbar_color: '401197' })
  server.create('department', {
    name: 'Ocean Beach Clinic',
    navbar_color: '122778',
  })
  server.create('department', {
    name: 'Park Animal Hospital',
    navbar_color: '0f5361',
  })
  server.create('department', {
    name: 'Balboa Pet Hospital',
    navbar_color: '614700',
  })
}
